import { createApp, watch } from 'vue';
import App from './App.vue';
import { createPinia } from 'pinia';
import router from './router/index';
import { useAuthStore } from './pinia/authStore'; // Adjust the path to your auth store
import {
  configureApiInterceptors,
  setAuthTokenProvider,
  setUnauthorizedHandler
} from './api/httpClient';

const app = createApp(App);
const pinia = createPinia();
app.use(pinia);
app.use(router);

// Perform an initial check of the authentication status
const authStore = useAuthStore();
const publicRoutes = ['Home', 'Help'];
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
let idleTimerId = null;

const redirectIfUnauthed = () => {
  const current = router.currentRoute.value;
  if (!authStore.isAuthenticated && !publicRoutes.includes(current?.name)) {
    router.push({ name: 'Home' });
  }
};

setAuthTokenProvider(() => authStore.token);
setUnauthorizedHandler(() => {
  authStore.setToken(null);
  redirectIfUnauthed();
});
configureApiInterceptors();

watch(
  () => authStore.isAuthenticated,
  () => {
    redirectIfUnauthed();
  }
);

const resetIdleTimer = () => {
  if (!authStore.isAuthenticated) return;
  if (idleTimerId) {
    window.clearTimeout(idleTimerId);
  }
  idleTimerId = window.setTimeout(() => {
    authStore.logout();
  }, IDLE_TIMEOUT_MS);
};

const stopIdleTimer = () => {
  if (idleTimerId) {
    window.clearTimeout(idleTimerId);
    idleTimerId = null;
  }
};

if (typeof window !== 'undefined') {
  window.__authStore = authStore;
  window.__resetIdleTimer = resetIdleTimer;
  window.__idleTimeoutMs = IDLE_TIMEOUT_MS;
  window.__setIdleTimeoutMs = (ms) => {
    if (!Number.isFinite(ms) || ms <= 0) {
      console.warn('[IdleTimer] Invalid timeout value', ms);
      return;
    }
    window.__idleTimeoutMs = ms;
    if (idleTimerId) {
      window.clearTimeout(idleTimerId);
      idleTimerId = window.setTimeout(() => {
        authStore.logout();
      }, ms);
    }
  };
}

watch(
  () => authStore.isAuthenticated,
  (isAuthed) => {
    if (isAuthed) {
      resetIdleTimer();
    } else {
      stopIdleTimer();
    }
  },
  { immediate: true }
);

['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'].forEach((eventName) => {
  window.addEventListener(eventName, resetIdleTimer, { passive: true });
});
authStore.checkAuthStatus().then(() => {
  // Mount the app after checking the auth status
  app.mount('#app');
});

// Re-check the authentication status when the window gains focus
window.addEventListener('focus', () => {
  authStore.checkAuthStatus();
  resetIdleTimer();
});

window.setInterval(() => {
  authStore.checkAuthStatus();
}, 5 * 60 * 1000);
